import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, Injector, NgZone, OnDestroy, inject } from '@angular/core';
import { AddinCommand } from '@model/ftf-api.model';
import { Store } from '@ngrx/store';
import { convertFileToBase64 } from '@utils/file-utils';
import { LoadedOutlookMailItem, MailItem, OutlookItemType, OutlookMailItem } from '@utils/office-utils';
import { BehaviorSubject, catchError, debounceTime, from, mergeMap, of, ReplaySubject, Subject, takeUntil, toArray } from 'rxjs';

import {
  DocumentDownload,
  FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS,
  FirstToFileFeatureFlags,
  FirstToFileWidgetXProps,
  IPTrifoldTypes,
  IPTypes,
  TrifoldFilters,
} from '../model/widget.model';
import { OFFICE_HOST_TYPE, WIDGET_HOST_TYPE } from '../tokens';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { LoaderService } from './loader.service';
import { LoggerService } from './logger.service';
import { setFeatureFlags, setLoaded } from '../../store/actions/feature-flags.actions';

export interface SelectedItems {
  items: MailItem[];
  exceedsLimit: boolean;
  count: number;
}
export const MAX_SELECTED_EMAILS = 20;

export enum FTFEvent {
  Load = 'load',
  DocAction = 'docAction',
  Command = 'command',
  CaseOpen = 'caseOpen',
}

@Injectable({
  providedIn: 'root',
})
export class AddinContextService implements OnDestroy {
  private readonly type = inject<Office.HostType | null>(OFFICE_HOST_TYPE);
  private readonly widgetLoaded = inject(WIDGET_HOST_TYPE);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private loader = inject(LoaderService);
  private breakpointObserver = inject(BreakpointObserver);
  private store = inject(Store);
  private loggerService = inject(LoggerService);

  // Subjects and Observables
  private $ngDestroyed = new Subject<void>();
  private selectedItemsChanged = new Subject<void>();
  private _outlookItems = new BehaviorSubject<SelectedItems>({ items: [], exceedsLimit: false, count: 0 });
  outlookItems$ = this._outlookItems.asObservable();
  private xpropsSubject = new Subject<FirstToFileWidgetXProps>();
  private xprops$ = this.xpropsSubject.asObservable();

  // store commands that need to be processed
  private commandQueue: AddinCommand[] = [];
  // subject that just notifies that there is a command to process
  private commandSubject = new ReplaySubject<void>(1);
  command$ = this.commandSubject.asObservable();

  // Subject for Office drag and drop events
  private _officeDragDrop = new Subject<Office.DragoverEventData | Office.DropEventData>();
  officeDragDrop$ = this._officeDragDrop.asObservable();

  // Services
  private _authService!: AuthService;
  private _apiService!: ApiService;

  // Other properties
  supportsMultiSelect = false;
  xprops: FirstToFileWidgetXProps | null = null;
  isSmallScreen = true;
  featureFlags?: FirstToFileFeatureFlags;

  /**
   * Get the AuthService instance.
   * @returns The AuthService instance.
   */
  private get authService(): AuthService {
    if (!this._authService) {
      this._authService = this.injector.get(AuthService);
    }
    return this._authService;
  }

  /**
   * Get the ApiService instance.
   * @returns The ApiService instance.
   */
  private get apiService(): ApiService {
    if (!this._apiService) {
      this._apiService = this.injector.get(ApiService);
    }
    return this._apiService;
  }

  /**
   * Get the Outlook items.
   * @returns The Outlook items.
   */
  get outlookItems(): MailItem[] {
    return this._outlookItems.getValue().items;
  }

  get outlookItem(): MailItem | null {
    const items = this.outlookItems;
    return items.length > 0 ? items[0] : null;
  }

  constructor() {
    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(takeUntil(this.$ngDestroyed))
      .subscribe((result) => {
        // If the screen matches small or xsmall breakpoints, it's a small screen
        this.isSmallScreen = result.matches;
      });
  }

  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  initialize() {
    if (this.isOfficeActive) {
      this.registerOfficeEvents();
    } else if (this.isWidgetActive) {
      this.registerWidgetEvents();
    }

    // if not a widget feature flags are already initialized
    if (!this.isWidgetActive) {
      this.store.dispatch(setLoaded({ isLoaded: true }));
    }
  }

  /**
   * Register widget events.
   * @returns {void}
   */
  registerWidgetEvents() {
    // Zoid widgets communicate with parent via a global xprops object
    // if there are no xprops then there is no widget
    if (!('xprops' in window)) return;

    // subscribe to future xprops changes
    this.xprops$.subscribe((xprops) => {
      if (this.xprops == null) {
        if (xprops.onLoad) {
          // trigger load callback 1st time
          xprops.onLoad();
        }
        // setup auth callback
        if (xprops.onAuth) {
          this.authService.canCallApi$.pipe(takeUntil(this.$ngDestroyed)).subscribe((canCallApi) => {
            if (canCallApi && xprops.onAuth) {
              xprops.onAuth();
            }
          });
        }
      }
      this.xprops = xprops;

      // update feature flags in store
      const newFeatureFlags = FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS.reduce((acc, key) => {
        acc[key] = xprops.featureFlags?.[key] ?? true;
        return acc;
      }, {} as FirstToFileFeatureFlags);
      // if feature flags haven't changed, do nothing (we can use stringify as the flags are always in same order due to the reduce order)
      if (JSON.stringify(this.featureFlags ?? {}) === JSON.stringify(newFeatureFlags ?? {})) return;
      this.featureFlags = newFeatureFlags;
      this.store.dispatch(setFeatureFlags({ featureFlags: newFeatureFlags }));
    });

    const { xprops } = window;
    // parent can update any of the properties and we can subscribe to changes via a callback
    if (xprops.onProps) {
      xprops.onProps((props: Partial<Pick<FirstToFileWidgetXProps, keyof FirstToFileWidgetXProps>>) =>
        this.ngZone.run(() => {
          const newXProps = { ...this.xprops, ...props };
          this.xpropsSubject.next(newXProps);
        })
      );
    }
    // trigger initial xprop event
    this.xpropsSubject.next(xprops);

    // register export functions
    if (xprops.export) {
      xprops.export({
        downloadDocument: (id: number, name: string, type: IPTypes): Promise<DocumentDownload> => {
          return new Promise((resolve, reject) => {
            this.apiService
              .downloadFile(id, name, type)
              .pipe(mergeMap((blob) => convertFileToBase64(blob)))
              .subscribe({
                next: (base64) => resolve({ id, fileName: name, base64 }),
                error: (error) => {
                  this.loggerService.error('[AddinContextService] Error downloading document:', error);
                  reject('Error downloading FTF document');
                },
              });
          });
        },
        openByCaseRefNum: (caseRefNum: string, type?: IPTrifoldTypes, filters?: TrifoldFilters) =>
          this.ngZone.run(() => {
            this.addCommands({ type: 'openByCaseRefNum', args: [caseRefNum, type, filters] });
            this.commandSubject.next();
            return Promise.resolve();
          }),
        openByAppSerNum: (appSerNum: string, type?: IPTrifoldTypes, filters?: TrifoldFilters) => {
          this.addCommands({ type: 'openByAppSerNum', args: [appSerNum, type, filters] });
          this.commandSubject.next();
          return Promise.resolve();
        },
      });
    }
  }

  /**
   * Registers event handlers for Office events, specifically for Outlook.
   *
   * Office does not always allow for many of its events to be unsubscribed from, so we need to be careful
   * to only define them once.  The AddinContextService class is only created once, so we can safely register
   * them here.
   *
   * @private
   * @memberof AddinContextService
   */
  registerOfficeEvents() {
    // register events for outlook
    if (typeof Office !== 'undefined' && this.type === Office.HostType.Outlook) {
      // dialogs don't have mailbox context
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((Office.context as any).isDialog) return;

      // Check if the load by id outlook command we use for multiselect is supported
      // Right now this isn't working on Desktop, but it is on web.  Holding off on doing the check
      // until 1.15 has been out a little longer for possible bug to be fixed
      this.supportsMultiSelect = true; //Office.context.requirements.isSetSupported('Mailbox', '1.15');
      this.registerOutlookEvents();
    }
  }

  /**
   * Registers event handlers for Outlook-specific events, particularly for handling selected items.
   *
   * This method sets up a debounced loading of selected items and registers handlers for item changes
   * and selection changes in the Outlook mailbox context.
   *
   * @private
   * @memberof AddinContextService
   */
  registerOutlookEvents() {
    // Set up the debounced loading of items
    this.selectedItemsChanged.pipe(debounceTime(300), takeUntil(this.$ngDestroyed)).subscribe(() => {
      this.loggerService.debug('[AddinContextService] Selected items changed, loading items...');
      this.loadMultiSelectItems();
    });
    // Trigger initial load
    this.selectedItemsChanged.next();

    const { mailbox } = Office.context;
    /*
        ItemChanged -  Triggered when pinned, and when a single item selected is changed
            Right now SelectedItemsChanged is triggering for everything, but leaving this in incase
            that is a bug that gets fixed.  Debounce will handle the multiple triggers
        */
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => this.selectedItemsChanged.next());
    // Register for selection changes
    mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      () => this.selectedItemsChanged.next(),
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          this.loggerService.error('[AddinContextService] Problem registering selected items handler:', result.error);
        }
      }
    );
    mailbox.addHandlerAsync(Office.EventType.DragAndDropEvent, (event: Office.DragAndDropEventArgs) => {
      this.ngZone.run(() => {
        this._officeDragDrop.next(event.dragAndDropEventData);
      });
    });
  }

  /**
   * Loads the currently selected items in the Outlook mailbox.
   *
   * @returns {void}
   */
  loadMultiSelectItems() {
    try {
      this.loader.show();
      // if outlook doesn't support 1.15 api, then just load the current item (if any)
      if (!this.supportsMultiSelect) {
        this.loadCurrentItem();
        return;
      }
      Office.context.mailbox.getSelectedItemsAsync(this.onMultiSelectedItems.bind(this));
    } catch (ex) {
      this.loader.hide();
      if (ex instanceof Error && /Elevated permission is required to call the method/.test(ex.message)) {
        // This error occurs when you are using pinning mode and start with a single non multi select
        // version of addin.. then switch to multi select.  The deregister / register of th event
        // should handle this condition
      } else if (
        ex instanceof Error &&
        /Invalid operation \(getSelectedItemsAsync\) when Office.context.mailbox.item is null/.test(ex.message)
      ) {
        // This error is normal and happens when you deselect all mail
        this._outlookItems.next({ items: [], exceedsLimit: false, count: 0 });
      } else {
        this.loggerService.error('[AddinContextService] Error loading selected items:', ex);
        this._outlookItems.next({ items: [], exceedsLimit: false, count: 0 });
      }
    }
  }

  /**
   * Handles the result of the getSelectedItemsAsync call.
   * @param asyncResult The result of the async call.
   * @returns {void}
   */
  private onMultiSelectedItems(asyncResult: Office.AsyncResult<Office.SelectedItemDetails[]>) {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      this.loggerService.error(`[AddinContextService] Problem loading selected items ${asyncResult.error.message}`);
      this._outlookItems.next({ items: [], exceedsLimit: false, count: 0 });
      return;
    }

    const { mailbox } = Office.context;
    // Check to see if we should be treating this as a single item selection
    if (
      mailbox.item &&
      asyncResult.value.length === 1 &&
      // do the ids match
      (mailbox.item.itemId === asyncResult.value[0].itemId ||
        // or if we are in compose mode and the itemId is null (new email)
        (asyncResult.value[0].itemMode === 'Compose' && !mailbox.item.itemId))
    ) {
      this.loadCurrentItem();
      return;
    }

    // we want to only take the 1st from each conversationId
    const selectedItems = asyncResult.value.filter(
      (item, index, self) => index === self.findIndex((t) => t.conversationId === item.conversationId)
    );

    const exceedsLimit = selectedItems.length > MAX_SELECTED_EMAILS;
    if (exceedsLimit) {
      this.loggerService.debug(
        `[AddinContextService] More than ${MAX_SELECTED_EMAILS} items were selected. Only processing the first ${MAX_SELECTED_EMAILS} items.`
      );
    }
    const limitedItems = selectedItems.slice(0, MAX_SELECTED_EMAILS);

    // Fetch all the items via LoadedOutlookMailItem
    const prevItems = this._outlookItems.getValue().items;
    const itemsObs = limitedItems.map((item) => {
      // do we already have it cached?
      const existingItem = prevItems.find((oi) => oi instanceof LoadedOutlookMailItem && oi.itemId === item.itemId);
      if (existingItem) {
        return of(existingItem);
      } else {
        // otherwise fetch it
        return LoadedOutlookMailItem.fromOutlookItemId(item.itemId, this.loggerService).pipe(
          catchError((error) => {
            this.loggerService.error(`[AddinContextService] Error loading item ${item.itemId}:`, error);
            // Return a default value or an empty observable in case of error
            return of(null);
          })
        );
      }
    });

    // Wait for all items to be loaded
    from(itemsObs)
      .pipe(
        mergeMap((itemObs) => itemObs, 1),
        toArray()
      )
      .subscribe({
        next: (items) => {
          // Filter out any null values that were returned due to errors
          const successfulItems = items.filter((item) => item !== null) as LoadedOutlookMailItem[];
          this.loggerService.debug('[AddinContextService] Selected Items:', successfulItems);
          this._outlookItems.next({ items: successfulItems, exceedsLimit, count: selectedItems.length });
        },
        error: (error) => {
          this.loggerService.error('[AddinContextService] Error loading Outlook items by ID:', error);
          this._outlookItems.next({ items: [], exceedsLimit: false, count: 0 });
        },
        complete: () => {
          this.loader.hide();
        },
      });
  }

  /**
   * Loads the currently selected item in the Outlook mailbox.
   * @returns {void}
   */
  loadCurrentItem() {
    if (!Office.context.mailbox.item) {
      this._outlookItems.next({ items: [], exceedsLimit: false, count: 0 });
      return;
    }
    // if there is a single item selected and it is the same as the current item
    OutlookMailItem.fromOutlookItemType(Office.context.mailbox.item as OutlookItemType, this.loggerService).subscribe((item) => {
      this.loggerService.debug('[AddinContextService] Selected Item:', [item]);
      this._outlookItems.next({ items: [item], exceedsLimit: false, count: 1 });
      this.loader.hide();
    });
  }

  /**
   * Records an event with the given data.
   * @param event The event to record.
   * @param data The data associated with the event.
   */
  recordEvent(event: FTFEvent, data: any) {
    if (this.isWidgetActive) {
      switch (event) {
        case FTFEvent.Load:
          this.xprops?.onLoad?.();
          break;
        case FTFEvent.DocAction:
          this.xprops?.onDocumentAction?.(data);
          break;
        case FTFEvent.CaseOpen:
          this.xprops?.onCaseOpen?.(data);
          break;
        case FTFEvent.Command:
          this.xprops?.onCommand?.(data.type, data.args);
          break;
      }
    }
  }

  /**
   * Adds commands to the command queue.
   * @param command The commands to add.
   */
  addCommands(...command: AddinCommand[]) {
    this.commandQueue.push(...command);
    this.commandSubject.next();
  }

  /**
   * Dequeues all commands from the command queue.
   * @returns {AddinCommand[]} The dequeued commands.
   */
  dequeueCommands(): AddinCommand[] {
    return this.commandQueue.splice(0, this.commandQueue.length);
  }

  /**
   * Gets whether the current layout is vertical.
   *
   * @readonly
   * @type {boolean}
   */
  get isVerticalLayout(): boolean {
    if (this.isWidgetActive) {
      const orientation = this.xprops?.orientation;
      return orientation === 'Vertical' || ((!orientation || orientation === 'Auto') && this.isSmallScreen);
    }

    return this.isSmallScreen;
  }

  /**
   * Gets whether the current layout is horizontal.
   *
   * @readonly
   * @type {boolean}
   */
  get isHorizontalLayout(): boolean {
    return !this.isVerticalLayout;
  }

  /**
   * Gets whether the current Office context is active.
   *
   * @readonly
   * @type {boolean}
   */
  get isOfficeActive(): boolean {
    return this.type !== null;
  }

  /**
   * Gets whether the current widget is active.
   *
   * @readonly
   * @type {boolean}
   */
  get isWidgetActive(): boolean {
    return this.widgetLoaded;
  }

  /**
   * Gets whether the current Host type is Word.
   *
   * @readonly
   * @type {boolean}
   */
  get isWord() {
    return typeof Office !== 'undefined' && this.type === Office.HostType.Word;
  }

  /**
   * Gets whether the current Host type is Excel.
   *
   * @readonly
   * @type {boolean}
   */
  get isExcel() {
    return typeof Office !== 'undefined' && this.type === Office.HostType.Excel;
  }

  /**
   * Gets whether the current Host type is Outlook.
   *
   * @readonly
   * @type {boolean}
   */
  get isOutlook() {
    return typeof Office !== 'undefined' && this.type === Office.HostType.Outlook;
  }

  /**
   * Gets whether the current Outlook item is in compose mode.
   *
   * @readonly
   * @type {boolean}
   */
  get isOutlookCompose() {
    if (!this.isOutlook) return false;
    const { outlookItems } = this;
    return outlookItems.length > 0 ? outlookItems[0]?.mode === 'compose' : false;
  }

  /**
   * Gets whether the current Outlook item is in read mode.
   *
   * @type {boolean}
   */
  get isOutlookRead() {
    if (!this.isOutlook) return false;
    const { outlookItems } = this;
    return outlookItems.length > 0 ? outlookItems[0]?.mode === 'read' : false;
  }

  /**
   * Gets the host type
   *
   * @readonly
   * @type {(Office.HostType | null)}
   */
  get hostType(): Office.HostType | null {
    return this.type;
  }
}
